import React, { useState, useRef, useEffect } from 'react';
import { usePreferences, TableKey } from '../PreferencesContext';

interface ColumnDef {
  key: string;
  label: string;
}

interface ColumnToggleProps {
  tableKey: TableKey;
  columns: ColumnDef[];
}

const ColumnToggle: React.FC<ColumnToggleProps> = ({ tableKey, columns }) => {
  const { columnVisibility, toggleColumn, saveStatus } = usePreferences();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const vis = columnVisibility[tableKey] || {};
  const visibleCount = Object.values(vis).filter(Boolean).length;
  const totalCols = columns.length;
  const hiddenCount = totalCols - visibleCount;

  return (
    <div className="column-toggle" ref={containerRef}>
      <button
        className="btn btn-secondary btn-small column-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        title="Show/hide table columns"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
        </svg>
        Columns{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
      </button>
      {open && (
        <div className="column-toggle-dropdown">
          <div className="column-toggle-header">Toggle Columns</div>
          {columns.map((col) => {
            const checked = vis[col.key] !== false;
            const disabled = checked && visibleCount <= 2;
            return (
              <label
                key={col.key}
                className={`column-toggle-item ${disabled ? 'disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleColumn(tableKey, col.key)}
                />
                <span>{col.label}</span>
              </label>
            );
          })}
          <div className={`column-toggle-footer column-toggle-status-${saveStatus}`}>
            {saveStatus === 'saving' && (
              <>
                <svg className="column-toggle-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Saving...
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Preferences saved
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                Failed to save
              </>
            )}
            {saveStatus === 'idle' && (
              <span className="column-toggle-hint">Changes are saved automatically</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnToggle;
